import { AudioTrack } from '../types';

/**
 * Fills gaps between audio tracks by shifting them leftward (earlier in time)
 * to remove any empty space between consecutive tracks.
 * 
 * All tracks are considered together regardless of lane.
 * 
 * Algorithm:
 * 1. Sort tracks by startTime
 * 2. For each track, calculate accumulated shift from all previous gaps
 * 3. Apply shift to move tracks left, closing gaps
 * 4. Return new array with updated positions
 * 
 * @param tracks - Array of audio tracks to process
 * @returns New array of audio tracks with gaps filled
 */
export const fillAudioGaps = (
  tracks: AudioTrack[]
): AudioTrack[] => {
  // Handle edge cases
  if (!tracks || tracks.length === 0) {
    return tracks;
  }

  if (tracks.length === 1) {
    // Single track: move to start at 0 if not already
    const track = tracks[0];
    if (track.startTime === 0) {
      return tracks;
    }
    return [{
      ...track,
      startTime: 0
    }];
  }

  // Phase 1: Sort by start time
  const sortedTracks = [...tracks].sort((a, b) => a.startTime - b.startTime);

  // Phase 2 & 3: Calculate shifts and apply them
  // Initialize shift with first track's start time to move everything to begin at 0
  let shiftAmount = sortedTracks[0].startTime;
  const newTracks: AudioTrack[] = [];

  for (let i = 0; i < sortedTracks.length; i++) {
    const track = sortedTracks[i];
    
    // Apply accumulated shift to this track
    const newStartTime = Math.max(0, track.startTime - shiftAmount);
    
    const newTrack: AudioTrack = {
      ...track,
      startTime: newStartTime
    };
    
    newTracks.push(newTrack);
    
    // Calculate gap to next track (if exists)
    if (i < sortedTracks.length - 1) {
      const nextStartTime = sortedTracks[i + 1].startTime;
      const currentEndTime = track.startTime + track.duration; // Use original for gap calculation
      
      // If there's a gap, accumulate it for subsequent tracks
      if (nextStartTime > currentEndTime) {
        const gap = nextStartTime - currentEndTime;
        shiftAmount += gap;
      }
    }
  }

  return newTracks;
};
